import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Key, Power, Copy, Printer, Trash } from "@phosphor-icons/react";

export default function EmployeeAccounts() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(null);
  const [credential, setCredential] = useState(null);

  const load = useCallback(() => {
    api.get("/employee-accounts").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/employee-accounts", { employee_id: form.employee_id, email: form.email });
      setCredential({ id: data.id, email: data.email, password: data.temp_password, title: "Account creato" });
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reset = async (r) => {
    if (!window.confirm(`Generare una nuova password di sistema per ${r.email}?`)) return;
    try {
      const { data } = await api.post(`/employee-accounts/${r.id}/reset-password`);
      setCredential({ id: r.id, email: data.email, password: data.temp_password, title: "Password reimpostata" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const printSheet = async (c) => {
    try {
      const res = await api.post(`/employee-accounts/${c.id}/credentials-pdf`,
        { password: c.password, site_url: window.location.origin }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/employee-accounts/${r.id}`, { active: !(r.active !== false) }); toast.success("Account aggiornato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    const who = r.employee ? `${r.employee.last_name} ${r.employee.first_name}` : r.name;
    if (!window.confirm(`Eliminare definitivamente l'account di ${who} (${r.email})?\n\nNon potrà più accedere. Il dipendente resta in anagrafica.`)) return;
    try { await api.delete(`/employee-accounts/${r.id}`); toast.success("Account eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const taken = new Set(rows.map((r) => r.employee_id));
  const available = employees.filter((e) => !taken.has(e.id));

  return (
    <div data-testid="employee-accounts-page" className="space-y-3 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Account Dipendenti</h1>
          <p className="text-sm text-muted-foreground mt-1">
            La password provvisoria viene mostrata al momento della creazione o del reset dell'account: il dipendente dovrà cambiarla al primo accesso.
          </p>
        </div>
        <Button size="sm" className="rounded-sm font-semibold" data-testid="add-account-btn"
          onClick={() => setForm({ employee_id: "", email: "" })}>
          <Plus size={15} className="mr-1.5" /> Nuovo account
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="accounts-table">
          <thead className="bg-secondary">
            <tr>{["", "Dipendente", "Email", "Codice", "Reparto", "Password", "Stato"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`account-row-${r.email}`}>
                <td className="px-3 py-1.5">
                  <div className="flex justify-start gap-1">
                    <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => reset(r)} data-testid={`reset-${r.email}`}>
                      <Key size={14} className="mr-1" /> Reset password
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => toggle(r)}
                      title={r.active === false ? "Riattiva accesso" : "Nega accesso"} data-testid={`toggle-account-${r.email}`}>
                      <Power size={15} className={r.active === false ? "text-emerald-600" : "text-rose-600"} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => remove(r)}
                      title="Elimina definitivamente l'account" data-testid={`delete-account-${r.email}`}>
                      <Trash size={15} className="text-rose-600" />
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold">{r.employee ? `${r.employee.last_name} ${r.employee.first_name}` : r.name}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.email}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.employee?.code || "—"}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.employee?.department || "—"}</td>
                <td className="px-3 py-1.5">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${r.must_change_password
                    ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300"
                    : "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"}`}>
                    {r.must_change_password ? "DA CAMBIARE" : "IMPOSTATA"}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-xs">{r.active === false ? "Disattivato" : "Attivo"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="accounts-empty">Nessun account dipendente creato</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="account-dialog">
            <DialogHeader><DialogTitle className="font-head">Nuovo account dipendente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
                <Select value={form.employee_id} onValueChange={(v) => {
                  const e = available.find((x) => x.id === v);
                  setForm({
                    employee_id: v,
                    email: e ? `${e.first_name}.${e.last_name}`.toLowerCase().replace(/\s+/g, "") + "@azienda.it" : "",
                  });
                }}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="account-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {available.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Email di accesso</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="account-email" />
              </div>
              <p className="text-xs text-muted-foreground">
                La password provvisoria viene generata automaticamente e mostrata subito dopo la creazione.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="account-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={create} disabled={!form.employee_id || !form.email} data-testid="account-save">Crea account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {credential && (
        <Dialog open onOpenChange={() => setCredential(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="credential-dialog">
            <DialogHeader><DialogTitle className="font-head">{credential.title}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Comunica queste credenziali al dipendente oppure stampa il foglio da consegnargli. Al primo accesso gli verrà richiesto di cambiare la password.
            </p>
            <div className="rounded-sm border border-border bg-secondary p-3 space-y-1.5 text-sm">
              <div><span className="text-muted-foreground">Sito:</span> <b>{window.location.origin}</b></div>
              <div><span className="text-muted-foreground">Email:</span> <b data-testid="credential-email">{credential.email}</b></div>
              <div><span className="text-muted-foreground">Password provvisoria:</span> <b className="tabular-nums" data-testid="credential-password">{credential.password}</b></div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" data-testid="credential-copy"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin} — ${credential.email} / ${credential.password}`); toast.success("Credenziali copiate"); }}>
                <Copy size={14} className="mr-1.5" /> Copia
              </Button>
              <Button variant="outline" className="rounded-sm font-semibold" data-testid="credential-print"
                onClick={() => printSheet(credential)}>
                <Printer size={14} className="mr-1.5" /> Stampa foglio PDF
              </Button>
              <Button className="rounded-sm font-semibold" onClick={() => setCredential(null)} data-testid="credential-close">Ho annotato</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
